import { useState } from 'react';
import { Copy, MoreHorizontal, QrCode } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { QRCodeCanvas } from '@/components/ui/qrcode';
import { usePayto } from '@/hooks/usePayto';
import {
  PRIVATE_COINS,
  isValidPaytoAddress,
  paytoLabel,
  paytoUri,
  truncateAddr,
} from '@/lib/payto';

interface PaytoButtonsProps {
  pubkey: string;
}

/**
 * Renders payment-target chips (NIP-A3 kind 10133).
 * <=3 entries: inline chips. >3: first 2 + "More coins (N)" dropdown.
 * Lightning entries are skipped here (rendered as ZapButton separately).
 */
export function PaytoButtons({ pubkey }: PaytoButtonsProps) {
  const { data: entries, isLoading } = usePayto(pubkey);

  const [qr, setQr] = useState<{ label: string; uri: string; address: string } | undefined>();
  const [copied, setCopied] = useState<string | undefined>();

  if (isLoading) return null;

  const nonLightning = (entries ?? []).filter((e) => e.type !== 'lightning');
  if (nonLightning.length === 0) return null;

  const inline = nonLightning.length <= 3 ? nonLightning : nonLightning.slice(0, 2);
  const overflow = nonLightning.length > 3 ? nonLightning.slice(2) : [];

  const copy = async (address: string) => {
    await navigator.clipboard.writeText(address);
    setCopied(address);
    setTimeout(() => setCopied(undefined), 1500);
  };

  const chip = (entry: (typeof nonLightning)[number]) => {
    const isPrivate = PRIVATE_COINS.has(entry.type);
    const valid = isValidPaytoAddress(entry.address);
    return (
      <span key={`${entry.type}-${entry.address}`} className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={() => valid && copy(entry.address)}
          onContextMenu={(e) => {
            e.preventDefault();
            if (valid) setQr({ label: paytoLabel(entry.type), uri: paytoUri(entry.type, entry.address), address: entry.address });
          }}
          title={
            isPrivate
              ? `${paytoLabel(entry.type)} — private tip, not counted publicly. Click to copy.`
              : `${paytoLabel(entry.type)} — click to copy.`
          }
          className="text-[12px] px-2 py-0.5 border border-gray-300 dark:border-[#3a3d41] rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#1a1c1e] inline-flex items-center gap-1"
        >
          <span>{paytoLabel(entry.type)}</span>
          <span className="text-gray-400 dark:text-gray-500">{truncateAddr(entry.address)}</span>
          {copied === entry.address ? (
            <span className="text-green-600 dark:text-green-400 text-[11px]">copied!</span>
          ) : (
            <Copy className="size-3 text-gray-400 dark:text-gray-500" />
          )}
        </button>
        <button
          type="button"
          onClick={() => {
            if (valid) setQr({ label: paytoLabel(entry.type), uri: paytoUri(entry.type, entry.address), address: entry.address });
          }}
          title={`Show ${paytoLabel(entry.type)} QR code`}
          aria-label={`Show ${paytoLabel(entry.type)} QR code`}
          className="p-1 rounded text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#232528]"
        >
          <QrCode className="size-3.5" />
        </button>
      </span>
    );
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {inline.map(chip)}

        {overflow.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-6 px-2 text-[12px] rounded-full"
              >
                <MoreHorizontal className="size-3.5 mr-1" />
                More coins ({overflow.length})
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {overflow.map((entry) => (
                <DropdownMenuItem
                  key={`${entry.type}-${entry.address}`}
                  onClick={() => copy(entry.address)}
                  className="text-[12px]"
                >
                  {paytoLabel(entry.type)} · {truncateAddr(entry.address)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <Dialog open={Boolean(qr)} onOpenChange={(o) => !o && setQr(undefined)}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="text-[16px]">{qr?.label} address</DialogTitle>
          </DialogHeader>
          {qr && (
            <div className="flex flex-col items-center gap-3">
              <QRCodeCanvas value={qr.uri} size={200} className="rounded" />
              <p className="text-[12px] text-gray-700 dark:text-gray-300 break-all text-center font-mono">
                {qr.address}
              </p>
              <Button
                type="button"
                size="sm"
                onClick={() => navigator.clipboard.writeText(qr.address)}
                className="bg-[#f97316] hover:bg-[#ea580c] dark:bg-[#fb923c] dark:hover:bg-[#fdba74] dark:text-[#431407] text-white text-[12px]"
              >
                <Copy className="size-3.5 mr-1" /> Copy address
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
